import { UserState } from 'src/app/state/user/user.state';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  AGENT_SETTINGS_METADATA,
  AgentSettings,
} from '../../interfaces/agent-settings';
import {
  Observable,
  Subject,
  debounceTime,
  filter,
  take,
  takeUntil,
} from 'rxjs';
import { Actions, Select, Store, ofActionDispatched } from '@ngxs/store';
import { PredictorModelActions } from 'src/app/state/predictor-model/predictor-model.actions';
import { PredictorModelState } from 'src/app/state/predictor-model/predictor-model.state';
import { Slider } from 'primeng/slider';
import { AppConfig, APP_CONFIG } from '../../../appconfig/appconfig';
import { ConfigurationProfile } from '../../interfaces/configuration-profile';

@Component({
  selector: 'app-agent-settings-section',
  templateUrl: './agent-settings-section.component.html',
  styleUrls: ['./agent-settings-section.component.scss'],
})
export class AgentSettingsSectionComponent implements OnInit, OnDestroy {
  debounceTime = 750;

  constructor(
    private store: Store,
    private actions$: Actions,
    @Inject(APP_CONFIG) private appConfig: AppConfig
  ) {}

  @Select(PredictorModelState.selectedSimulationId)
  selectedSimulationId$!: Observable<string | null>;

  @Select(PredictorModelState.configurationProfiles)
  configurationProfiles$!: Observable<ConfigurationProfile[]>;

  modelChange$ = new Subject<AgentSettings>();

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  selectedConfigurationProfile: ConfigurationProfile | undefined;
  selectConfigurationProfile(event: {
    originalEvent: Event;
    value: ConfigurationProfile;
  }) {
    this.model = { ...event.value.agentSettings };
    this.modelChange$.next(this.model);
  }

  createNewConfigurationProfile() {
    if (this.configurationForm.invalid) return;

    this.store.dispatch(
      new PredictorModelActions.CreateConfigurationProfile({
        agentSettings: this.model!,
        terrainId: this.store.selectSnapshot(
          PredictorModelState.selectedTerrainId
        )!,
        name: this.configurationModel.name,
        description: this.configurationModel.description,
      })
    );
    this.configurationForm.reset();
  }

  ngOnInit(): void {
    this.setModelToStoreValue();
    this.listenForModelChange();
    this.listenForAgentReset();
    this.listenForStudyAreaChange();
    this.sliderReference$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((slider: Slider) => {
        this.sliderReference = slider;
      });
    this.removeDebugFeaturesIfNotConfigured();
  }

  @Select(UserState.debuggerAccess) debuggerAccess$!: Observable<boolean>;

  removeDebugFeaturesIfNotConfigured() {
    this.debuggerAccess$.pipe(take(1)).subscribe((enableDebugFeatures) => {
      if (this.fields != null && !enableDebugFeatures) {
        this.fields = this.fields.filter(
          (field) =>
            field.key !== 'showAgents' && field.key !== 'removeRandomness'
        );
      }
    });
  }

  setModelToStoreValue() {
    this.model = {
      ...this.store.selectSnapshot(PredictorModelState.agentSettings),
    };
  }

  listenForStudyAreaChange() {
    this.store
      .select(PredictorModelState.studyAreaAgentSettings)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe({
        next: (agentSettings: AgentSettings | undefined) => {
          if (!agentSettings) {
            return;
          }
          this.model = agentSettings;
          this.resetAgentSettingDropdown();
        },
      });
  }

  resetAgentSettingDropdown() {
    this.selectedConfigurationProfile = undefined;
    this.placeholderText = 'Custom Configuration';
  }

  listenForModelChange() {
    this.modelChange$
      .pipe(
        debounceTime(this.debounceTime),
        filter(
          () =>
            this.store.selectSnapshot(
              PredictorModelState.selectedSimulationId
            ) !== null
        ),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((model) => {
        this.store.dispatch(
          new PredictorModelActions.UpdateAgentSettings(
            model,
            this.store.selectSnapshot(PredictorModelState.selectedSimulationId)!
          )
        );
      });
  }

  listenForAgentReset() {
    this.actions$
      .pipe(
        ofActionDispatched(PredictorModelActions.ResetAgentSettings),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => this.setModelToStoreValue());
  }

  placeholderText: string = 'Custom Configuration';
  onModelChange(model: AgentSettings) {
    this.modelChange$.next(model);
    this.resetAgentSettingDropdown();
  }

  form = new FormGroup({});
  model: AgentSettings | undefined;
  sliderReference$ = new Subject<Slider>();
  sliderReference: Slider | undefined;

  fields: FormlyFieldConfig[] = [
    {
      className: 'basin-mb-0',
      fieldGroupClassName: 'basin-display-flex basin-column-gap-4 basin-mt-4',
      fieldGroup: [
        {
          key: 'fanMaxSlope',
          className: 'basin-width-80',
          type: 'slider',
          props: {
            label: AGENT_SETTINGS_METADATA.fanMaxSlope?.label,
            tooltip: AGENT_SETTINGS_METADATA.fanMaxSlope?.tooltip,
            min: 0,
            max: 90,
          },
        },
        {
          key: 'fanMaxSlope',
          className: 'basin-column-gap-4 basin-width-20 basin-mt-3',
          type: 'textbox-units',
          wrappers: ['focus-next-wrapper'],
          props: {
            prepend: '',
            append: '°',
            min: 0,
            max: 90,
            updateOnBlurOnly: true,
          },
        },
      ],
    },
    {
      className: 'basin-mb-0',
      fieldGroupClassName: 'basin-display-flex basin-column-gap-4',
      fieldGroup: [
        {
          key: 'fanMinΣ',
          className: 'basin-width-80',
          type: 'slider',
          props: {
            label: AGENT_SETTINGS_METADATA.fanMinΣ?.label,
            tooltip: AGENT_SETTINGS_METADATA.fanMinΣ?.tooltip,
            min: 0.2,
            max: 0.8,
            step: 0.01,
          },
        },
        {
          key: 'fanMinΣ',
          className: 'basin-column-gap-4 basin-width-20 basin-mt-3',
          type: 'textbox',
          wrappers: ['focus-next-wrapper'],
          props: {
            min: 0.2,
            max: 0.8,
            blur: (field) => {
              const value: number = field.formControl?.value;
              if (value < 0.2) {
                field.formControl?.setValue(0.2);
              } else if (value > 0.8) {
                field.formControl?.setValue(0.8);
              }
            },
          },
        },
      ],
    },
    {
      className: 'basin-mb-0',
      fieldGroupClassName: 'basin-display-flex basin-column-gap-4',
      fieldGroup: [
        {
          key: 'fanMaxΣ',
          className: 'basin-width-80',
          type: 'slider',
          props: {
            label: AGENT_SETTINGS_METADATA.fanMaxΣ?.label,
            tooltip: AGENT_SETTINGS_METADATA.fanMaxΣ?.tooltip,
            min: 0.8,
            max: 6,
            step: 0.02,
            sliderReference: this.sliderReference$,
          },
          expressionProperties: {
            'props.min': (model) => model.fanMinΣ,
            'model.fanMaxΣ': (model) => {
              if (
                model.fanMaxΣ &&
                model.fanMinΣ &&
                model.fanMaxΣ < model.fanMinΣ
              ) {
                return model.fanMinΣ;
              }
              this.sliderReference?.updateHandleValue();
              return model.fanMaxΣ;
            },
          },
        },
        {
          key: 'fanMaxΣ',
          className: 'basin-column-gap-4 basin-width-20 basin-mt-3',
          type: 'textbox',
          wrappers: ['focus-next-wrapper'],
          props: {
            min: 0.8,
            max: 6,
            blur: (field) => {
              const value: number = field.formControl?.value;
              if (value < field.props!.min!) {
                field.formControl?.setValue(field.props!.min!);
              } else if (value > 6) {
                field.formControl?.setValue(6);
              }
            },
          },
          expressionProperties: {
            'props.min': (model) => model.fanMinΣ,
          },
        },
      ],
    },
    {
      className: 'basin-mb-0',
      fieldGroupClassName: 'basin-display-flex basin-column-gap-4',
      fieldGroup: [
        {
          key: 'fanExponent',
          className: 'basin-width-80',
          type: 'slider',
          props: {
            label: AGENT_SETTINGS_METADATA.fanExponent?.label,
            tooltip: AGENT_SETTINGS_METADATA.fanExponent?.tooltip,
            min: 0.1,
            max: 5,
            step: 0.02,
          },
        },
        {
          key: 'fanExponent',
          className: 'basin-column-gap-4 basin-width-20 basin-mt-3',
          type: 'textbox',
          wrappers: ['focus-next-wrapper'],
          props: {
            min: 0.1,
            max: 5,
            blur: (field) => {
              const value: number = field.formControl?.value;
              if (value < 0.1) {
                field.formControl?.setValue(0.1);
              } else if (value > 5) {
                field.formControl?.setValue(5);
              }
            },
          },
        },
      ],
    },
    {
      className: 'basin-mb-0',
      fieldGroupClassName: 'basin-display-flex basin-column-gap-4',
      fieldGroup: [
        {
          key: 'maxSpawns',
          className: 'basin-width-80',
          type: 'slider',
          props: {
            label: AGENT_SETTINGS_METADATA.maxSpawns?.label,
            tooltip: AGENT_SETTINGS_METADATA.maxSpawns?.tooltip,
          },
        },
        {
          key: 'maxSpawns',
          className: 'basin-column-gap-4 basin-width-20 basin-mt-3',
          type: 'textbox',
          wrappers: ['focus-next-wrapper'],
          props: {
            min: 0,
            max: 100,
            blur: (field) => {
              const value: number = field.formControl?.value;
              if (value < 0) {
                field.formControl?.setValue(0);
              } else if (value > 100) {
                field.formControl?.setValue(100);
              }
            },
          },
        },
      ],
    },
    {
      className: 'basin-mb-0',
      fieldGroupClassName: 'basin-display-flex basin-column-gap-4',
      fieldGroup: [
        {
          key: 'depositionMultiplier',
          className: 'basin-width-80',
          type: 'slider',
          props: {
            label: AGENT_SETTINGS_METADATA.depositionMultiplier?.label,
            tooltip: AGENT_SETTINGS_METADATA.depositionMultiplier?.tooltip,
            min: 0.1,
            max: 2,
            step: 0.1,
          },
        },
        {
          key: 'depositionMultiplier',
          className: 'basin-column-gap-4 basin-width-20 basin-mt-3',
          type: 'textbox-units',
          wrappers: ['focus-next-wrapper'],
          props: {
            prepend: '',
            append: 'x',
            min: 0.1,
            max: 2,
            updateOnBlurOnly: true,
          },
        },
      ],
    },
    {
      className: 'basin-mb-0',
      fieldGroupClassName: 'basin-display-flex basin-column-gap-4',
      fieldGroup: [
        {
          key: 'erosionMultiplier',
          className: 'basin-width-80',
          type: 'slider',
          props: {
            label: AGENT_SETTINGS_METADATA.erosionMultiplier?.label,
            tooltip: AGENT_SETTINGS_METADATA.erosionMultiplier?.tooltip,
            min: 0.1,
            max: 2,
            step: 0.1,
          },
        },
        {
          key: 'erosionMultiplier',
          className: 'basin-column-gap-4 basin-width-20 basin-mt-3',
          type: 'textbox-units',
          wrappers: ['focus-next-wrapper'],
          props: {
            prepend: '',
            append: 'x',
            min: 0.1,
            max: 2,
            updateOnBlurOnly: true,
          },
        },
      ],
    },
    {
      className: 'basin-mb-0 basin-display-none',
      fieldGroupClassName: 'basin-display-flex basin-column-gap-4',
      fieldGroup: [
        {
          key: 'lossPercent',
          className: 'basin-width-80',
          type: 'slider',
          props: {
            label: 'Mass Loss Per 45° Turn',
            min: 0,
            max: 0,
            step: 0.1,
            readonly: true,
          },
        },
        {
          key: 'lossPercent',
          className: 'basin-column-gap-4 basin-width-20 basin-mt-3',
          type: 'textbox-units',
          wrappers: ['focus-next-wrapper'],
          props: {
            prepend: '',
            append: '%',
            min: 0,
            max: 0,
            readonly: true,
            updateOnBlurOnly: true,
          },
        },
      ],
    },
    {
      className: 'basin-mb-0',
      fieldGroupClassName: 'basin-display-flex basin-column-gap-4',
      fieldGroup: [
        {
          key: 'minInitDepth',
          className: 'basin-width-80',
          type: 'slider',
          props: {
            label: AGENT_SETTINGS_METADATA.minInitDepth?.label,
            tooltip: AGENT_SETTINGS_METADATA.minInitDepth?.tooltip,
            min: 0,
            max: 5,
            step: 0.25,
          },
        },
        {
          key: 'minInitDepth',
          className: 'basin-column-gap-4 basin-width-20 basin-mt-3',
          type: 'textbox-units',
          wrappers: ['focus-next-wrapper'],
          props: {
            prepend: '',
            append: 'm',
            min: 0,
            max: 5,
            updateOnBlurOnly: true,
          },
        },
      ],
    },
    {
      className: 'basin-display-flex basin-column-gap-4',
      key: 'removeRandomness',
      type: 'checkboxToggle',
      templateOptions: {
        hideLabel: true,
        label: 'Remove Randomness',
      },
    },
  ];

  configurationFields: FormlyFieldConfig[] = [
    {
      className: 'basin-text-align-left basin-mb-4',
      key: 'name',
      type: 'textbox',
      props: {
        label: 'Configuration Profile Name',
        placeholder: 'Name',
        required: true,
      },
    },
    {
      fieldGroupClassName: 'p-d-flex',
      className: 'basin-mb-0',
      fieldGroup: [
        {
          className: 'basin-width-100 ',
          key: 'description',
          type: 'basin-textarea',
          templateOptions: {
            label: 'Description',
            placeholder: 'Enter Description',
            required: true,
            autoResize: true,
            maxlength: 150,
          },
        },
      ],
    },
  ];

  configurationForm = new FormGroup({});
  configurationModel: { name: string; description: string } = {
    name: '',
    description: '',
  };
}
